/**
 * Taksonomi kegagalan Embedded Signup.
 *
 * Tujuannya satu: mengubah sinyal Meta yang tidak seragam menjadi sekumpulan
 * kode tetap, supaya UI bisa memberi **satu langkah berikutnya yang konkret**
 * alih-alih menyalurkan pesan Meta apa adanya ke pemilik toko.
 *
 * ⚠️ **Batas keandalan yang harus diketahui sebelum dipercaya.** Meta tidak
 * menerbitkan daftar kode error bernomor untuk Embedded Signup. Yang sampai ke
 * browser hanyalah `error_message` berbahasa Inggris, `error_id`, `event`, dan
 * `current_step`. Karena itu klasifikasi di bawah bekerja dengan **pencocokan
 * pola pada teks**, dan pola-pola itu **disusun dari bentuk pesan yang lazim,
 * belum diverifikasi terhadap error Meta sungguhan** — verifikasi itu lingkup
 * `S4-OPS-02`, yang menguji pada nomor nyata.
 *
 * Konsekuensi desainnya: pola dibuat **sempit dan menuntut konteks**, bukan
 * longgar. Salah klasifikasi lebih berbahaya daripada `UNKNOWN` — pengguna yang
 * disuruh memperbarui aplikasi padahal masalahnya verifikasi bisnis akan
 * membuang percobaan, dan onboarding coexistence tidak bisa diulang bebas.
 * `UNKNOWN` punya pesan yang tetap membantu, jadi jatuh ke sana bukan kegagalan.
 */

export const SIGNUP_ERROR_CODES = [
  // Bukan klasifikasi pesan Meta — datang langsung dari error_code backend
  // saat WABA_COEXISTENCE_ENABLED mati (S5-APP-05). Ditaruh di taksonomi yang
  // sama supaya wizard punya satu jalur penanganan, bukan dua.
  "COEXISTENCE_DISABLED",
  "NUMBER_IN_OTHER_WABA",
  "APP_VERSION_TOO_OLD",
  "BUSINESS_NOT_VERIFIED",
  "WRONG_PORTFOLIO",
  "NUMBER_NOT_IN_APP",
  "PERMISSIONS_DECLINED",
  "QR_EXPIRED",
  "SESSION_EXPIRED",
  "POPUP_BLOCKED",
  "CANCELLED",
  "NOT_CONFIGURED",
  "UNKNOWN",
] as const;

export type SignupErrorCode = (typeof SIGNUP_ERROR_CODES)[number];

/** Layar wizard yang jadi titik ulang setelah gagal. `null` = mengulang tidak akan menolong. */
export type RetryTarget = "choose" | "prereq" | "guide" | "connect" | null;

export interface SignupErrorSignals {
  /** `event` dari payload WA_EMBEDDED_SIGNUP: FINISH · CANCEL · ERROR. */
  event?: string;
  /** `current_step` dari payload — menunjukkan di layar mana pengguna berhenti. */
  currentStep?: string;
  /** `error_message` mentah dari Meta. Bahasa Inggris, tidak untuk ditampilkan. */
  errorMessage?: string;
  /** `error_id` mentah dari Meta. Berguna saat pengguna menghubungi support. */
  errorId?: string;
  /** Diisi pemanggil saat popup gagal dibuka — tidak datang dari Meta. */
  popupBlocked?: boolean;
}

/**
 * Pola diurutkan dari yang paling menuntut konteks ke yang paling longgar.
 * Setiap pola wajib memuat kata yang mengunci topiknya — `expired` saja tidak
 * cukup untuk menyimpulkan QR atau sesi, karena "payment method expired" juga
 * mengandung kata itu.
 */
const MESSAGE_MATCHERS: ReadonlyArray<readonly [RegExp, SignupErrorCode]> = [
  [/not\s+found\s+in\s+the\s+whatsapp\s+business\s+app/i, "NUMBER_NOT_IN_APP"],
  [/already\s+registered.*another\s+whatsapp\s+business\s+account/i, "NUMBER_IN_OTHER_WABA"],
  [/another\s+whatsapp\s+business\s+account/i, "NUMBER_IN_OTHER_WABA"],
  [/business\s+verification/i, "BUSINESS_NOT_VERIFIED"],
  [/business.*not\s+verified/i, "BUSINESS_NOT_VERIFIED"],
  [/(newer|latest)\s+version|update\s+your\s+whatsapp\s+business\s+app/i, "APP_VERSION_TOO_OLD"],
  [/(declined|denied|rejected).*permission|permission.*(declined|denied|rejected)/i, "PERMISSIONS_DECLINED"],
  [/qr\s*code.*expired|expired.*qr\s*code/i, "QR_EXPIRED"],
  [/session\s+(has\s+)?expired|expired\s+session/i, "SESSION_EXPIRED"],
  [/business\s+portfolio/i, "WRONG_PORTFOLIO"],
];

/** Langkah tempat pengguna memilih Business Portfolio; batal di sini berarti salah pilih. */
const PORTFOLIO_STEPS = new Set(["BUSINESS_PORTFOLIO_SELECTION", "BUSINESS_SELECTION", "WABA_SELECTION"]);

export function classifySignupError(signals: SignupErrorSignals): SignupErrorCode {
  if (signals.popupBlocked) return "POPUP_BLOCKED";

  if (signals.errorMessage) {
    for (const [pattern, code] of MESSAGE_MATCHERS) {
      if (pattern.test(signals.errorMessage)) return code;
    }
  }

  // Batal di langkah pemilihan portfolio hampir selalu berarti salah pilih —
  // pengguna mundur karena melihat nama bisnis yang bukan miliknya.
  if (signals.event === "CANCEL" && signals.currentStep && PORTFOLIO_STEPS.has(signals.currentStep)) {
    return "WRONG_PORTFOLIO";
  }

  if (signals.event === "CANCEL") return "CANCELLED";

  return "UNKNOWN";
}

/**
 * Kasus yang tidak bisa diselesaikan pengguna sendiri dari dalam aplikasi.
 * Untuk kasus ini UI menawarkan jalur support alih-alih tombol coba lagi yang
 * hanya akan gagal dengan cara yang sama.
 */
const SUPPORT_REQUIRED: ReadonlySet<SignupErrorCode> = new Set<SignupErrorCode>([
  "BUSINESS_NOT_VERIFIED",
  "NUMBER_IN_OTHER_WABA",
  "NOT_CONFIGURED",
]);

export function needsSupport(code: SignupErrorCode): boolean {
  return SUPPORT_REQUIRED.has(code);
}

/**
 * Titik ulang per kode. Sengaja tidak selalu `choose` — memaksa pengguna
 * mengulang dari awal setelah QR kedaluwarsa membuang waktu tanpa alasan.
 *
 * Nilainya bebas-jalur: wizard yang menyesuaikan target ini ke jalur nomor baru,
 * yang tidak punya layar prasyarat maupun panduan.
 */
const RETRY_TARGETS: Readonly<Record<SignupErrorCode, RetryTarget>> = {
  // Keadaan normal, bukan kegagalan — mengulang tidak mengubah apa pun sampai
  // flag dinyalakan lagi di backend.
  COEXISTENCE_DISABLED: null,
  // Cukup ulangi pemindaian — keadaan akun tidak berubah.
  QR_EXPIRED: "guide",
  SESSION_EXPIRED: "guide",
  WRONG_PORTFOLIO: "guide",
  PERMISSIONS_DECLINED: "guide",
  POPUP_BLOCKED: "guide",
  CANCELLED: "guide",
  UNKNOWN: "guide",
  // Versinya disebut di layar prasyarat — ke sanalah pengguna perlu kembali.
  APP_VERSION_TOO_OLD: "prereq",
  // Coexistence tidak berlaku untuk nomor ini; jalur lain yang benar.
  NUMBER_NOT_IN_APP: "choose",
  // Mengulang tidak akan mengubah apa pun sampai ada tindakan di luar aplikasi.
  BUSINESS_NOT_VERIFIED: null,
  NUMBER_IN_OTHER_WABA: null,
  NOT_CONFIGURED: null,
};

export function retryTargetFor(code: SignupErrorCode): RetryTarget {
  return RETRY_TARGETS[code];
}

export interface SignupErrorContext {
  metaMessage?: string;
  metaErrorId?: string;
  currentStep?: string;
}

/**
 * Kegagalan Embedded Signup yang sudah terklasifikasi.
 *
 * `message` sengaja **bukan** teks Meta: teks itu berbahasa Inggris, ditulis
 * untuk pengembang, dan kerap menyebut istilah internal Meta. Yang ditampilkan
 * ke pengguna diambil dari next-intl berdasarkan `code`; `metaMessage` dan
 * `metaErrorId` hanya untuk log dan untuk disertakan saat menghubungi support.
 */
export class EmbeddedSignupError extends Error {
  readonly code: SignupErrorCode;
  readonly metaMessage?: string;
  readonly metaErrorId?: string;
  readonly currentStep?: string;

  constructor(code: SignupErrorCode, context: SignupErrorContext = {}) {
    super(`Embedded Signup gagal: ${code}`);
    this.name = "EmbeddedSignupError";
    this.code = code;
    this.metaMessage = context.metaMessage;
    this.metaErrorId = context.metaErrorId;
    this.currentStep = context.currentStep;
  }

  /** Bentuk ringkas untuk log — tidak pernah dipakai sebagai teks di layar. */
  toLogPayload(): Record<string, unknown> {
    return {
      code: this.code,
      metaErrorId: this.metaErrorId,
      metaMessage: this.metaMessage,
      currentStep: this.currentStep,
    };
  }
}
